import { useEffect, useState } from 'react'
import { PLATFORMS, type PlatformId } from '@/lib/platforms'
import type { Channel } from '@/lib/types'

const sizes = { xs: 22, sm: 28, md: 36, lg: 48 }

export function channelProfileSource(channel: Channel) {
  return (
    channel.avatarUrl ||
    `${import.meta.env.VITE_API_BASE || ''}/api/data/channels/${channel.id}/avatar`
  )
}

export function ProfileImage({
  src,
  name,
  background,
  size,
}: {
  src?: string | null
  name: string
  background: string
  size: number
}) {
  const [failed, setFailed] = useState(false)

  useEffect(() => setFailed(false), [src])

  return (
    <span
      className="relative grid shrink-0 place-items-center overflow-hidden rounded-full font-display font-semibold text-white"
      style={{ width: size, height: size, background, fontSize: size * 0.4 }}
      aria-label={`${name} profile picture`}
      data-avatar-source={src?.startsWith('blob:') ? 'proxied' : src ? 'remote' : 'fallback'}
    >
      {name.slice(0, 1).toUpperCase()}
      {src && !failed && (
        <img
          src={src}
          alt=""
          referrerPolicy="no-referrer"
          className="absolute inset-0 h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      )}
    </span>
  )
}

export function PlatformBadge({
  platform,
  size = 14,
}: {
  platform: PlatformId
  size?: number
}) {
  const p = PLATFORMS[platform]
  return (
    <span
      className="inline-flex items-center justify-center rounded-full text-white ring-2 ring-surface"
      style={{ width: size + 8, height: size + 8, background: p.color === '#ffffff' ? '#111' : p.color }}
    >
      <span style={{ width: size, height: size }} className="inline-flex">
        {p.icon}
      </span>
    </span>
  )
}

export function ChannelAvatar({
  channel,
  size = 'md',
  showBadge = true,
}: {
  channel: Channel
  size?: keyof typeof sizes
  showBadge?: boolean
}) {
  const dim = sizes[size]
  const p = PLATFORMS[channel.platform]
  return (
    <div className="relative shrink-0" style={{ width: dim, height: dim }}>
      <ProfileImage
        src={channelProfileSource(channel)}
        name={channel.name}
        background={channel.avatarColor || p.gradient}
        size={dim}
      />
      {showBadge && (
        <span
          className="absolute -bottom-1 -right-1 grid place-items-center rounded-full ring-2 ring-surface"
          style={{
            width: dim * 0.5,
            height: dim * 0.5,
            background: p.color === '#ffffff' ? '#111' : p.color,
          }}
        >
          <span style={{ width: dim * 0.3, height: dim * 0.3 }} className="inline-flex text-white">
            {p.icon}
          </span>
        </span>
      )}
    </div>
  )
}
